import { Button } from 'lego-on-react';
import React, { useEffect, useReducer, useState } from 'react';

import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';

import { ApiError, getMerchants, registerBilling } from '../../../api';
import { MerchantInfo } from '../../../model/billing';
import { Skill } from '../../../model/skill';
import { enableBillingTooltipReducer } from '../../../reducers/billing';
import { Loading } from '../../Loading/Loading';
import TooltipButton from '../../TooltipButton/TooltipButton';
import { PageHeadline } from '../../Typo/Typo';
import MerchantRequestForm from '../MerchantRequestForm/MerchantRequestForm';
import MerchantsTable from '../MerchantsTable/MerchantsTable';
import PublicKeyField from '../PublicKeyField/PublicKeyField';

interface ContentProps {
    skill: Skill;
    merchants: MerchantInfo[];
    afterRequest: (merchantInfo: MerchantInfo) => void;
    disabled: boolean;
}

const BillingPageContent: React.FC<ContentProps> = ({ skill, merchants, afterRequest, disabled }) => {
    return (
        <div>
            <MerchantRequestForm skill={skill} afterRequest={afterRequest} merchants={merchants} disabled={disabled} />
            <PublicKeyField skill={skill} />
            <MerchantsTable merchants={merchants} />
        </div>
    );
};

interface RequestButtonProps {
    setBillingEnabled: (billingEnabled: boolean) => void;
    skillId: string;
}

const RequestButton: React.FC<RequestButtonProps> = ({ skillId, setBillingEnabled }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [tooltipProps, dispatchTooltipAction] = useReducer(enableBillingTooltipReducer, {
        tooltipVisible: false,
    });

    const onClick = async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        try {
            await registerBilling(skillId);
            setBillingEnabled(true);
        } catch (e) {
            console.error(e);
            dispatchTooltipAction('error');
            setIsLoading(false); // Сбрасываю загрузку только при ошибке, так как при успехе компонент анмаунтится
        }
    };

    return (
        <TooltipButton
            {...tooltipProps}
            size="m"
            onClick={onClick}
            onOutsideClick={() => dispatchTooltipAction('clear')}
            progress={isLoading}
        >
            Подключить оплату
        </TooltipButton>
    );
};

interface RetryProps {
    onClick: () => void;
}

const RetryComp: React.FC<RetryProps> = ({ onClick }) => {
    return (
        <div>
            <p>Произошла ошибка</p>
            <Button theme="action" size="m" onClick={onClick}>
                Повторить
            </Button>
        </div>
    );
};

interface Props {
    skill: Skill;
    disabled: boolean;
}

const BillingPage: React.FC<Props> = ({ skill, disabled }) => {
    const [merchants, setMerchants] = useState<MerchantInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [billingEnabled, setBillingEnabled] = useState(false);

    const loadMerchants = async () => {
        if (!skill.onAir || isLoading) {
            return;
        }

        setIsError(false);
        setIsLoading(true);

        try {
            setMerchants(await getMerchants(skill.id));
            setBillingEnabled(true);
        } catch (e) {
            if (e instanceof ApiError) {
                if (e.payload && e.payload.errorCode === 'BILLING_NOT_ENABLED') {
                    setBillingEnabled(false);

                    return;
                }
            }

            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const addMerchant = (merchant: MerchantInfo) => {
        setMerchants([merchant, ...merchants.filter((m) => m.entity_id !== merchant.entity_id)]);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void loadMerchants(), [billingEnabled]);

    if (!skill.onAir) {
        <div>
            <PageHeadline title="Оплата" />
            <div>Подключение оплат доступно только для опубликованных диалогов</div>
        </div>;
    }

    return (
        <ErrorBoundary withHomepageLink>
            <div>
                <PageHeadline title="Оплата" />
                {isLoading ? (
                    <Loading centered size="l" />
                ) : isError ? (
                    <RetryComp onClick={loadMerchants} />
                ) : !billingEnabled ? (
                    <RequestButton setBillingEnabled={setBillingEnabled} skillId={skill.id} />
                ) : (
                    <BillingPageContent
                        merchants={merchants}
                        skill={skill}
                        afterRequest={addMerchant}
                        disabled={disabled}
                    />
                )}
            </div>
        </ErrorBoundary>
    );
};

export default BillingPage;
